import { compose, filter, pathOr } from 'ramda'
import React from 'react'

import { useDebouncedFn } from '@/app/helpers'
import { usePersonalizationList } from '@/app/module/campaigns/hooks/use-personalization-list'
import type { ContactGroup } from '@/app/module/contacts/types'
import { useGetGroups } from '@/app/module/groups/hooks/use-get-groups'
import { useGetSegments } from '@/app/module/segments/hooks/use-get-segments'
import { useUpdateUser } from '@/app/module/user/hooks/use-update-user'
import { UserType } from '@/app/module/user/types'
import { PersonalizationType, SinglePersonalizationType } from '@/app/types'

import UserAccessDialog from './user-access-dialog'

type Props = {
  data: UserType

  onClose: () => void
}

const usePzList = () => {
  const pzList = usePersonalizationList()

  return React.useMemo(
    () => ({
      ...pzList,
      contact: compose<[PersonalizationType], SinglePersonalizationType[], SinglePersonalizationType[]>(
        filter<SinglePersonalizationType>((v) => v.value !== 'contact.id'),
        pathOr([], ['contact']),
      )(pzList),
    }),
    [pzList],
  )
}

/**
 * Container (interacts with redux store) for UserAccessModal
 */
const UserAccessContainer: React.FC<Props> = ({ data, onClose }) => {
  const personalizationList = usePzList()
  const [groupsQuery, setGroupsQuery] = React.useState({
    page: 1,
    size: 1000,
    search: '',
  })
  const [segmentsQuery, setSegmentsQuery] = React.useState({
    page: 1,
    size: 1000,
    search: '',
  })
  const { data: groupsData, isFetching: isGroupsFetching, isLoading: isGroupsLoading } = useGetGroups(groupsQuery)
  const {
    data: segmentsData,
    isFetching: isSegmentsFetching,
    isLoading: isSegmentsLoading,
  } = useGetSegments(segmentsQuery)

  const [updateUser, { isLoading: isUpdating }] = useUpdateUser()

  // We are getting the most recent 1000 groups and 1000 segments from the API since it's the limit
  const groups = React.useMemo(() => {
    const rawGroups = groupsData?.groups || []
    const rawSegments = segmentsData?.groups || []

    return [...rawGroups, ...rawSegments] as ContactGroup[]
  }, [groupsData?.groups, segmentsData?.groups])

  const handleSave = React.useCallback(
    async (item: UserType) => {
      const success = await updateUser(item.id, {
        fullAccess: item.fullAccess,
        notify: item.notify,
        readCustomFieldAll: item.readCustomFieldAll,
        readCustomFieldIds: item.readCustomFieldIds || undefined,
        readFields: item.readFields || undefined,
        readGroupAll: item.readGroupAll,
        readGroupIds: item.readGroupIds || undefined,
        role: item.role,
      })
      if (success) {
        onClose()
      }
    },
    [onClose, updateUser],
  )

  const searchGroups = React.useCallback((search: string) => {
    setGroupsQuery((prev) => ({
      ...prev,
      page: 1,
      search,
    }))
    setSegmentsQuery((prev) => ({
      ...prev,
      page: 1,
      search,
    }))
  }, [])

  const handleGroupsSearch = useDebouncedFn(searchGroups)

  const groupsLoading = isGroupsLoading || isGroupsFetching || isSegmentsLoading || isSegmentsFetching

  return (
    <UserAccessDialog
      data={data}
      groups={groups}
      groupsLoading={!isUpdating && groupsLoading}
      loading={isUpdating}
      personalizationList={personalizationList}
      onClose={onClose}
      onGroupsSearch={handleGroupsSearch}
      onSave={handleSave}
    />
  )
}

export default UserAccessContainer
